import { Component } from 'preact';
import { observer } from 'mobx-preact';
import { reaction } from 'mobx';
import Mousetrap from 'mousetrap';

/**
 * Hidden arcade trigger (C). Renders nothing. Mounted once from the stable app
 * shell via the components-other registry. While a user is logged in it listens
 * for the Konami code (desktop) and for 7 quick taps on the header logo (touch)
 * and opens the arcade cabinet drawer (game select). Never opens over an
 * existing drawer.
 */
export const KONAMI_SEQUENCE = 'up up down down left right left right b a';
export const LOGO_TAPS_REQUIRED = 7;
export const LOGO_TAP_WINDOW_MS = 3000;
export const ARCADE_DRAWER = 'arcade';

@observer
class ArcadeTrigger extends Component {
    constructor(props) {
        super(props);
        this.tapTimes = [];
        this.bound = false;
        this.disposeReaction = null;
    }

    componentDidMount() {
        this.sync();
    }

    componentDidUpdate() {
        this.sync();
    }

    componentWillUnmount() {
        this.teardown();
    }

    isLoggedIn() {
        const { userStore } = this.props.stores;
        return !!userStore?.user?.id;
    }

    sync() {
        if (this.isLoggedIn() && !this.bound) {
            this.setup();
        } else if (!this.isLoggedIn() && this.bound) {
            this.teardown();
        }
    }

    setup() {
        const { appState } = this.props.stores;
        Mousetrap.bind(KONAMI_SEQUENCE, this.open);
        this.disposeReaction = reaction(() => appState.logoClickCount, this.onLogoTap);
        this.bound = true;
    }

    teardown() {
        if (!this.bound) {
            return;
        }
        Mousetrap.unbind(KONAMI_SEQUENCE);
        if (this.disposeReaction) {
            this.disposeReaction();
            this.disposeReaction = null;
        }
        this.tapTimes = [];
        this.bound = false;
    }

    onLogoTap = () => {
        const now = Date.now();
        this.tapTimes = this.tapTimes.filter(t => now - t <= LOGO_TAP_WINDOW_MS);
        this.tapTimes.push(now);
        if (this.tapTimes.length >= LOGO_TAPS_REQUIRED) {
            this.tapTimes = [];
            this.open();
        }
    }

    open = () => {
        const { appState } = this.props.stores;
        if (appState.getTopMostDrawer() > 0) {
            return false;
        }
        appState.openDrawer(ARCADE_DRAWER, { height: appState.drawerHeightLarge }, 1);
        // false = Mousetrap preventDefault + stopPropagation
        return false;
    }

    render() {
        // Read the login state here so mobx-preact re-renders (and sync() runs)
        // when the user logs in or out; the component itself draws nothing.
        const { userStore } = this.props.stores;
        const loggedIn = !!userStore?.user?.id; // eslint-disable-line no-unused-vars
        return null;
    }
}

export default ArcadeTrigger;
